import { useState } from "react";

import type { Citation } from "../../types/agent-chat";
import {
  chipLabel,
  citationUrl,
  groupCitations,
  locatorSummary,
  type SourceGroup,
} from "./citationFormat";

export interface SourcesPanelProps {
  citations: Record<string, Citation>;
  citedIds: string[];
  unusedIds: string[];
  onOpenDocument?: (citation: Citation) => void;
}

/**
 * Sources under an answer, split into those the model cited and those it
 * merely looked at.
 *
 * The split is the honest part: showing every retrieved passage as a "source"
 * would imply the answer rests on all of them. The consulted-but-uncited group
 * is collapsed by default because it is diagnostic, not evidence.
 */
export function SourcesPanel({
  citations,
  citedIds,
  unusedIds,
  onOpenDocument,
}: SourcesPanelProps) {
  const [showUnused, setShowUnused] = useState(false);

  const resolve = (ids: string[]) => ids.map((id) => citations[id]).filter(Boolean);
  const cited = groupCitations(resolve(citedIds));
  const unused = groupCitations(resolve(unusedIds));

  if (cited.length === 0 && unused.length === 0) return null;

  return (
    <div className="mt-4 border-t border-brand-border pt-3">
      {cited.length > 0 && (
        <>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-brand-muted">
            Sources citées
          </h3>
          <ul className="mb-2 space-y-1">
            {cited.map((group) => (
              <SourceRow key={group.dedupeKey} group={group} onOpenDocument={onOpenDocument} />
            ))}
          </ul>
        </>
      )}

      {unused.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => setShowUnused((v) => !v)}
            aria-expanded={showUnused}
            className="text-xs text-brand-muted hover:text-brand-text"
          >
            {showUnused ? "▾" : "▸"} Consultées, non citées ({unused.length})
          </button>
          {showUnused && (
            <ul className="mt-1 space-y-1">
              {unused.map((group) => (
                <SourceRow
                  key={group.dedupeKey}
                  group={group}
                  muted
                  onOpenDocument={onOpenDocument}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function SourceRow({
  group,
  muted = false,
  onOpenDocument,
}: {
  group: SourceGroup;
  muted?: boolean;
  onOpenDocument?: (citation: Citation) => void;
}) {
  const url = citationUrl(group.representative);

  return (
    <li className={`flex items-baseline gap-2 text-sm ${muted ? "opacity-70" : ""}`}>
      <span className="flex-shrink-0 rounded bg-brand-surface px-1.5 text-[11px] font-semibold text-brand-muted">
        {group.ids.map(chipLabel).join(", ")}
      </span>
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer noopener"
          className="truncate text-brand-text hover:underline"
        >
          {group.title}
        </a>
      ) : (
        <button
          type="button"
          onClick={() => onOpenDocument?.(group.representative)}
          className="truncate text-left text-brand-text hover:underline"
        >
          {group.title}
        </button>
      )}
      <span className="ml-auto flex-shrink-0 text-[11px] text-brand-muted">
        {locatorSummary(group.representative)}
      </span>
    </li>
  );
}
